/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

/* eslint-disable no-duplicate-imports */
import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext
} from '@msdyn365-commerce/core';
import { CommerceListLine } from '@msdyn365-commerce/retail-proxy';
import { getByCustomerAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CommerceListsDataActions.g';
import { FullProductInput, getFullProducts, getProductDetailsCriteriaFromActionInput } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Input class for getActiveWishlistItems data action.
 */
export class ActiveWishlistInput implements IActionInput {
    public getCacheKey = () => 'ActiveWishlistItems';

    public getCacheObjectType = () => 'ActiveWishlistItems';

    public dataCacheType = (): CacheType => 'none';
}

const createInput = (inputData: ICreateActionContext) => {
    return new ActiveWishlistInput();
};

/**
 * Calls the Retail API and returns a CommerceList object based on the passed GetCartInput.
 * @param input
 * @param ctx
 */
export async function getActiveWishlistItems(input: ActiveWishlistInput, ctx: IActionContext): Promise<FullProduct[]> {
    // If no cart ID is provided in input, we need to create a cart object
    if (!input) {
        throw new Error('[getActiveWishlistItems]No valid Input was provided, failing');
    }

    if (ctx.requestContext.user.isAuthenticated) {
        const wishlists = await getByCustomerAsync({ callerContext: ctx, queryResultSettings: {} }, null);

        if (wishlists && wishlists.length > 0) {
            const productInputs: FullProductInput[] = [];
            wishlists.forEach(currWishlist => {
                if (currWishlist.CommerceListLines) {
                    currWishlist.CommerceListLines.forEach((commerceListLine: CommerceListLine) => {
                        if (commerceListLine.ProductId) {
                            productInputs.push(
                                new FullProductInput(
                                    commerceListLine.ProductId,
                                    ctx.requestContext.apiSettings,
                                    getProductDetailsCriteriaFromActionInput(ctx)
                                )
                            );
                        }
                    });
                }
            });

            if (productInputs.length > 0) {
                return getFullProducts(productInputs, ctx)
                    .then(products => {
                        if (products) {
                            const mappedProducts: any = [];
                            wishlists.forEach(currWishlist => {
                                if (currWishlist.CommerceListLines) {
                                    currWishlist.CommerceListLines.forEach((commerceListLine: CommerceListLine) => {
                                        const product: any = products.find((p: any) => {
                                            return commerceListLine.ProductId === p.ProductDetails.RecordId;
                                        });
                                        if (product && commerceListLine.ProductId) {
                                            const productInfo: any = { ...product };
                                            productInfo.lineId = commerceListLine.LineId;
                                            // find snapshotid...
                                            if (commerceListLine.ExtensionProperties && commerceListLine.ExtensionProperties.length > 0) {
                                                const snapShotId = commerceListLine.ExtensionProperties.find(e => {
                                                    return e.Key === 'AAA_SNAPSHOTID';
                                                });
                                                if (snapShotId && snapShotId.Value && snapShotId.Value.StringValue) {
                                                    // init into product..
                                                    productInfo.snapShotId = snapShotId.Value.StringValue;
                                                }

                                                // find price...
                                                const b2cPrice = commerceListLine.ExtensionProperties.find(e => {
                                                    return e.Key === 'AAA_B2CPRICE';
                                                });
                                                const b2bPrice = commerceListLine.ExtensionProperties.find(e => {
                                                    return e.Key === 'AAA_B2BPRICE';
                                                });
                                                if (b2cPrice && b2cPrice.Value && b2bPrice && b2bPrice.Value) {
                                                    // init into product..
                                                    productInfo.b2cPrice = b2cPrice.Value.DecimalValue;
                                                    productInfo.b2bPrice = b2bPrice.Value.DecimalValue;
                                                }
                                            }
                                            mappedProducts.push(productInfo);
                                        }
                                    });
                                }
                            });

                            return mappedProducts;
                        }
                        return <FullProduct[]>[];
                    })
                    .catch(error => {
                        ctx.trace(error);
                        ctx.telemetry.error(error.message);
                        ctx.telemetry.debug('[getActiveWishlistItems]Unable to hydrate cart with product information');
                        throw new Error('[getActiveWishlistItems]Unable to hydrate cart with product information');
                    });
            }
            ctx.trace('No productIds found for wishlist');
        } else {
            ctx.trace('[getActiveWishlistItems]Not able to get wishlists for the customer');
            return <FullProduct[]>[];
        }
    }
    ctx.trace('[getActiveWishlistItems]User token not found for wishlists');
    return <FullProduct[]>[];
}

export const getActiveWishlistItemsDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/wishlist/wishlist-items/get-items-in-wishlist',
    action: <IAction<FullProduct[]>>getActiveWishlistItems,
    input: createInput
});

export default getActiveWishlistItemsDataAction;
